import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import { api, readToken, TOKEN_KEY } from '../api/client';
import type { AuthSession, SigninInput, SignupInput, User } from '../api/types';
import { loginPath } from '../lib/next';
import { toast } from '../ui/toast';

const USER_KEY = 'cady_user';

interface AuthValue {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  login: (input: SigninInput) => Promise<User>;
  signup: (input: SignupInput) => Promise<User>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

function readUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function persist(token: string | null, user: User | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {
    /* storage unavailable */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState<string | null>(readToken);
  const [storedUser, setStoredUser] = useState<User | null>(readUser);

  const locRef = useRef(location);
  useEffect(() => {
    locRef.current = location;
  }, [location]);

  const clearSession = useCallback(() => {
    persist(null, null);
    setToken(null);
    setStoredUser(null);
    qc.cancelQueries();
    qc.clear();
  }, [qc]);

  // Central 401 handler: any authenticated request answered with 401 lands here.
  useEffect(() => {
    api.configure({
      onUnauthorized: () => {
        if (!readToken()) return;
        clearSession();
        toast.info('Your session has ended. Please sign in again.');
        const { pathname, search } = locRef.current;
        navigate(loginPath(pathname + search), { replace: true });
      },
    });
    return () => api.configure({ onUnauthorized: undefined });
  }, [clearSession, navigate]);

  // Keep tabs in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY) {
        setToken(e.newValue);
        if (!e.newValue) qc.clear();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [qc]);

  // Validate the session in the background. Non-401 errors (offline, 5xx) keep the session.
  const me = useQuery({
    queryKey: ['me', token],
    queryFn: () => api.me(),
    enabled: !!token,
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });

  useEffect(() => {
    // Remember the user for offline starts; `user` below already prefers me.data.
    if (me.data) persist(readToken(), me.data);
  }, [me.data]);

  const startSession = useCallback(
    (s: AuthSession) => {
      qc.clear();
      persist(s.accessToken, s.user);
      setToken(s.accessToken);
      setStoredUser(s.user);
      qc.setQueryData(['me', s.accessToken], s.user);
      return s.user;
    },
    [qc],
  );

  const login = useCallback(async (input: SigninInput) => startSession(await api.signin(input)), [startSession]);
  const signup = useCallback(async (input: SignupInput) => startSession(await api.signup(input)), [startSession]);

  const logout = useCallback(async () => {
    try {
      if (readToken()) await api.signout();
    } catch {
      /* best effort; we still drop the local session */
    }
    clearSession();
    navigate('/login', { replace: true });
  }, [clearSession, navigate]);

  const user = me.data ?? storedUser;
  const value = useMemo<AuthValue>(
    () => ({ token, user, isAuthenticated: !!token, login, signup, logout }),
    [token, user, login, signup, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
